import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';
import { makeStyles } from '@material-ui/core/styles';

import {
  renderTemplate,
  statusInfo,
  TEMPLATE_CATEGORIES,
  TEMPLATE_LANGUAGES,
} from '../../../constants/whatsapp';
import { actions as mainActions } from '../../Root/actions';
import { actions as whatsappActions } from '../actions';
import TemplateForm from '../components/TemplateForm';
import WhatsappPreview from '../components/WhatsappPreview';
import useRenderedImage from '../hooks/useRenderedImage';
import { NAME as WHATSAPP_NAME } from '../reducer';
import { colors } from '../theme';
import { KYIV_ZONE } from '../kyivTime';

function mapStateToProps(state) {
  return {
    ...state[WHATSAPP_NAME],
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...mainActions, ...whatsappActions }, dispatch);

const STATUS_COLORS = {
  ok: { backgroundColor: colors.okBg, color: colors.okText },
  warn: { backgroundColor: colors.warnBg, color: colors.warnText },
  error: { backgroundColor: '#fdecea', color: '#b71c1c' },
};

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
    borderRadius: 10,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  headTitle: {
    color: colors.text,
    fontWeight: 700,
    fontSize: '16px',
  },
  createButton: {
    backgroundColor: colors.accent,
    color: '#ffffff',
    fontWeight: 600,
    marginLeft: theme.spacing(1),
    '&:hover': { backgroundColor: colors.accentHover },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    padding: theme.spacing(1.75, 1.5),
    borderRadius: 8,
    cursor: 'pointer',
    borderBottom: `1px solid ${colors.border}`,
    '&:hover': { backgroundColor: colors.panelAlt },
  },
  rowActive: {
    backgroundColor: colors.accentSoft,
    '&:hover': { backgroundColor: colors.accentSoft },
  },
  rowTitle: {
    fontWeight: 600,
    color: colors.text,
  },
  rowMeta: {
    color: colors.textDim,
    fontSize: '12.5px',
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  editButton: {
    marginLeft: theme.spacing(1.5),
    padding: 6,
    backgroundColor: colors.accent,
    color: '#ffffff',
    '&:hover': { backgroundColor: colors.accentHover },
    '&.Mui-disabled': { backgroundColor: colors.border, color: '#ffffff' },
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
    padding: 6,
    backgroundColor: '#d32f2f',
    color: '#ffffff',
    '&:hover': { backgroundColor: '#b71c1c' },
  },
  reason: {
    color: '#b71c1c',
    fontSize: '12px',
    marginTop: theme.spacing(0.5),
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  error: {
    color: '#d32f2f',
    marginBottom: theme.spacing(1.5),
  },
  details: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: colors.panel,
    border: `1px solid ${colors.border}`,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    fontSize: '13px',
  },
  detailLabel: { color: colors.textDim },
  sticky: {
    position: 'sticky',
    top: theme.spacing(11),
  },
}));

const currentTime = () =>
  new Date().toLocaleTimeString('uk-UA', { timeZone: KYIV_ZONE, hour: '2-digit', minute: '2-digit' });

const categoryLabel = (value) => {
  const found = TEMPLATE_CATEGORIES.find((item) => item.value === value);
  return found ? found.label : value;
};

const languageLabel = (value) => {
  const found = TEMPLATE_LANGUAGES.find((item) => item.value === value);
  return found ? found.label : value;
};

// Пока шаблон на проверке, Meta не даёт его менять.
const EDITABLE_STATUSES = ['approved', 'rejected', 'paused'];

// Страница «Шаблоны»: список из Meta со статусами, превью, создание и редактирование.
function Templates(props) {
  const classes = useStyles();
  const { templates, templatesLoading, templatesError } = props;

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openingId, setOpeningId] = useState('');
  const [openError, setOpenError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    props.fetchTemplates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selected =
    templates.find((item) => item.id === selectedId) || templates[0] || null;

  const exampleValues = {};
  if (selected) {
    selected.params.forEach((param) => {
      exampleValues[param.key] = param.example || param.placeholder || '';
    });
  }
  const { url: imageUrl } = useRenderedImage(
    selected && selected.hasImage
      ? { template: { name: selected.templateName, language: selected.language }, values: exampleValues }
      : null
  );

  const handleCreate = async (template) => {
    const result = await props.createTemplate(template);
    if (result.ok) {
      props.showAlert({
        show: true,
        alertTitle: `Шаблон «${template.title}» отправлен на проверку`,
        alertText:
          'Meta проверяет шаблон — обычно от нескольких минут до суток. Статус появится в списке.',
        severity: 'success',
        style: {},
        variant: 'outlined',
      });
      setCreating(false);
      props.fetchTemplates();
    }
    return result;
  };

  // Форма редактирования открывается с тем, что сохранено на сервере, вместе с
  // картинкой и раскладкой текстов на ней.
  const openEdit = async (item) => {
    setOpeningId(item.id);
    setOpenError('');
    const result = await props.fetchTemplate(item.id);
    setOpeningId('');
    if (!result.ok) {
      setOpenError(result.error);
      return;
    }
    if (!result.data.editable) {
      setOpenError(`Шаблон «${item.title}» сейчас изменить нельзя: он на проверке в Meta или в нём есть то, что форма не поддерживает.`);
      return;
    }
    setEditing(result.data);
  };

  const handleUpdate = async (template) => {
    const result = await props.updateTemplate(editing.id, template);
    if (result.ok) {
      props.showAlert({
        show: true,
        alertTitle: `Изменения шаблона «${template.title}» отправлены на проверку`,
        alertText:
          'Meta снова проверяет шаблон — обычно от нескольких минут до суток. Статус появится в списке.',
        severity: 'success',
        style: {},
        variant: 'outlined',
      });
      setEditing(null);
      props.fetchTemplates();
    }
    return result;
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError('');
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    const result = await props.deleteTemplate(deleteTarget.id);
    setDeleting(false);
    if (!result.ok) {
      setDeleteError(result.error);
      return;
    }
    props.showAlert({
      show: true,
      alertTitle: `Шаблон «${deleteTarget.title}» удалён`,
      alertText: 'Отправлять его больше нельзя. Сообщения, которые уже ушли клиентам, остались у них.',
      severity: 'success',
      style: {},
      variant: 'outlined',
    });
    setDeleteTarget(null);
    setSelectedId('');
    props.fetchTemplates();
  };

  if (creating) {
    return (
      <TemplateForm onSubmit={handleCreate} onCancel={() => setCreating(false)} />
    );
  }

  if (editing) {
    return (
      <TemplateForm
        key={editing.id}
        initial={editing}
        onSubmit={handleUpdate}
        onCancel={() => setEditing(null)}
      />
    );
  }

  const previewText = selected
    ? renderTemplate(
        selected,
        selected.params.map((p) => p.example || p.placeholder || '')
      )
    : '';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Paper className={classes.card}>
          <div className={classes.head}>
            <Typography className={classes.headTitle}>
              Шаблоны сообщений
            </Typography>
            <div>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                disabled={templatesLoading}
                onClick={() => props.fetchTemplates()}
              >
                Обновить статусы
              </Button>
              <Button
                variant="contained"
                disableElevation
                className={classes.createButton}
                startIcon={<AddIcon />}
                onClick={() => setCreating(true)}
              >
                Создать шаблон
              </Button>
            </div>
          </div>

          {templatesError && (
            <Typography variant="body2" className={classes.error}>
              {templatesError}
            </Typography>
          )}

          {openError && (
            <Typography variant="body2" className={classes.error}>
              {openError}
            </Typography>
          )}

          {templatesLoading && templates.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Загружаем шаблоны из Meta…
            </Typography>
          )}

          {!templatesLoading && !templatesError && templates.length === 0 && (
            <div className={classes.empty}>
              <Typography variant="body1">
                Шаблонов пока нет. Создайте первый и отправьте его на проверку.
              </Typography>
            </div>
          )}

          {templates.map((item) => {
            const status = statusInfo(item.status);
            return (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                className={`${classes.row} ${
                  selected && selected.id === item.id ? classes.rowActive : ''
                }`}
                onClick={() => setSelectedId(item.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') setSelectedId(item.id);
                }}
              >
                <div>
                  <div className={classes.rowTitle}>{item.title}</div>
                  <div className={classes.rowMeta}>
                    {item.templateName} · {languageLabel(item.language)}
                  </div>
                  {item.rejectedReason && (
                    <div className={classes.reason}>
                      Причина отказа: {item.rejectedReason}
                    </div>
                  )}
                </div>
                <div className={classes.chips}>
                  <Chip
                    size="small"
                    className={classes.chip}
                    label={categoryLabel(item.category)}
                  />
                  <Chip
                    size="small"
                    className={classes.chip}
                    style={STATUS_COLORS[status.kind]}
                    label={status.label}
                  />
                  {item.supported && (
                    <IconButton
                      size="small"
                      aria-label="Редактировать шаблон"
                      title={
                        EDITABLE_STATUSES.includes(item.status)
                          ? 'Редактировать шаблон'
                          : 'Пока шаблон на проверке в Meta, менять его нельзя'
                      }
                      className={classes.editButton}
                      disabled={!EDITABLE_STATUSES.includes(item.status) || Boolean(openingId)}
                      onClick={(event) => {
                        event.stopPropagation();
                        openEdit(item);
                      }}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    aria-label="Удалить шаблон"
                    title="Удалить шаблон"
                    className={classes.deleteButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      setDeleteError('');
                      setDeleteTarget(item);
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <div className={classes.sticky}>
          <WhatsappPreview
            text={previewText}
            imageUrl={imageUrl}
            footer={selected ? selected.footer : ''}
            time={currentTime()}
            emptyText="Выберите шаблон в списке — здесь появится его превью."
            // Под превью карточка с названием, категорией и числом переменных.
            reserve={selected ? 160 : 0}
          />
          {selected && (
            <div className={classes.details}>
              <div className={classes.detailRow}>
                <span className={classes.detailLabel}>Название в Meta</span>
                <span>{selected.templateName}</span>
              </div>
              <div className={classes.detailRow}>
                <span className={classes.detailLabel}>Категория</span>
                <span>{categoryLabel(selected.category)}</span>
              </div>
              <div className={classes.detailRow}>
                <span className={classes.detailLabel}>Переменных</span>
                <span>{selected.params.length}</span>
              </div>
              {!selected.supported && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                  Этот шаблон нельзя отправлять из рассылок: в нём есть кнопки,
                  медиа или переменные в заголовке.
                </Typography>
              )}
            </div>
          )}
        </div>
      </Grid>

      <Dialog open={Boolean(deleteTarget)} onClose={closeDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Удалить шаблон?</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              {deleteTarget.title}
              <Typography component="span" variant="body2" color="textSecondary">
                {' '}
                ({deleteTarget.templateName})
              </Typography>
            </Typography>
          )}
          <Typography variant="body2" style={{ marginTop: 12 }}>
            Шаблон будет удалён в Meta, отправлять его станет нельзя. Это нельзя отменить.
            Сообщения, которые уже ушли клиентам, у них останутся.
          </Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            За удаление Meta денег не берёт. Подойдёт ли то же название для нового шаблона
            и когда, Meta не уточняет, при необходимости придумайте другое.
          </Typography>
          {deleteError && (
            <Typography variant="body2" style={{ marginTop: 12, color: '#d32f2f' }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={deleting}
            startIcon={<DeleteIcon />}
            style={{ backgroundColor: '#d32f2f', color: '#fff' }}
            onClick={handleDelete}
          >
            {deleting ? 'Удаляем…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Templates);
